import { gql } from '@common/hooks';
import { NotificationType } from '@identity/types';

export const CurrentMembershipContextQuery = gql`
  query currentMembershipContextQuery {
    currentMembership {
      id
      name
      role
      roleName
      allowEmailNotifications
      canCreateWorkforceGroupUnitRate
      canCreateShiftGaps
      canUploadShiftGaps
      canEditShiftGaps
      canUploadWorkers
      hasDashboards
      hasUkgTimecardsSyncEnabled
      authenticationIdleTimeoutInSeconds
      authenticationIdleTimeoutInText
      integrationsEnabled
      canApprovalsBypass {
        value
      }
      canApprovalsOverrideApprove {
        value
      }
      canApprovalsOverrideReject {
        value
      }
      canBypassTierVisibility {
        value
      }
      canCreateApplications {
        value
      }
      canCreateApprovalChainTemplates {
        value
      }
      canCreateBillingCard {
        value
      }
      canCreateOffers {
        value
      }
      canCreateWorkers {
        value
      }
      canEditIndividualComplianceList {
        value
      }
      canEditWorkers {
        value
      }
      canSeeStaffingRequestRates {
        value
      }
      canUpdateMembership {
        value
      }
      canUpdateMembershipAdmin {
        value
      }
      canShowAdminApprovalsItem {
        value
      }
      canShowAdminBilling {
        value
      }
      canShowAdminGroupsTab {
        value
      }
      canShowAdminMembers {
        value
      }
      canShowAdminOrganizationStructure {
        value
      }
      canShowAdminRatesOnDemandTab {
        value
      }
      canShowAdminRatesLongTermTab {
        value
      }
      canShowAdminStaffingRequestTemplates {
        value
      }
      canShowAdminSuppliers {
        value
      }
      canShowAdminTimekeepingHolidays {
        value
      }
      canShowAdminWorksListTemplates {
        value
      }
      canShowApplications {
        value
      }
      canShowApplicationsSubmitted {
        value
      }
      canShowAssignmentTermBillingRules {
        value
      }
      canShowDisputes {
        value
      }
      canShowDoNotRehires {
        value
      }
      canShowOffers {
        value
      }
      canShowGroupShiftMessaging {
        value
      }
      canShowInvoices {
        value
      }
      canShowLongTermAssignmentComments {
        value
      }
      canShowLongTermAssignments {
        value
      }
      canShowMissionControl {
        value
      }
      canShowOrganizationUnitIntegration {
        value
      }
      canShowOrganizationUnitShiftConfigs {
        value
      }
      canShowReverseInvoices {
        value
      }
      canShowShiftTargeting {
        value
      }
      canShowSidebarAdminItem {
        value
      }
      canShowSidebarBillingItem {
        value
      }
      canShowSidebarCandidatesItem {
        value
      }
      canShowSidebarOnboardingItem {
        value
      }
      canShowSidebarStaffingItem {
        value
      }
      canShowSidebarTeamItem {
        value
      }
      canShowSidebarTimesheetsItem {
        value
      }
      canShowTermsConditionsLink {
        value
      }
      canShowShifts {
        value
      }
      canShowTiering {
        value
      }
      canShowWorksLists {
        value
      }
      canShowWorkerStatus {
        value
      }
      canSubmitSubmittalPacketChangeRequest {
        value
      }
      canUpdateAdminUsersUnits {
        value
      }
      canUpdateOffers {
        value
      }
      canUpdateApplications {
        value
      }
      canUpdateStaffingRequests {
        value
      }
      canUpdateWorkerStatus {
        value
      }
      canUpdateStaffingRequestAssignments {
        value
      }
      canWithdrawApplications {
        value
      }
      canShowOrganizationOnDemandSettings {
        value
      }
      notificationCategories {
        key
        name
      }
      notifications {
        category
        enabledForCurrentMembership
        humanizedName
        notificationName
      }
      worksUser {
        id
        firstName
        lastName
        phone
        saveLastVisitedPage
        timeZone
        initials
        email
        intercomUserHash
        internalTrustedUser
      }
    }
  }
`;

export type CurrentMembershipContextQueryResponse = {
  currentMembership: CurrentMembershipPermissionsType;
};

export type CurrentMembershipPermissionsType = {
  id: string;
  name: string;
  role: string;
  roleName: string;
  allowEmailNotifications: boolean;
  canCreateApplications: boolean;
  canCreateApprovalChainTemplates: boolean;
  canCreateWorkforceGroupUnitRate: boolean;
  canCreateOffers: boolean;
  canCreateShiftGaps: boolean;
  canUploadShiftGaps: boolean;
  canEditShiftGaps: boolean;
  canUploadWorkers: boolean;
  hasDashboards: boolean;
  hasUkgTimecardsSyncEnabled: boolean;
  authenticationIdleTimeoutInSeconds: number;
  authenticationIdleTimeoutInText: string;
  integrationsEnabled: [string];
  canApprovalsBypass: boolean;
  canApprovalsOverrideApprove: boolean;
  canApprovalsOverrideReject: boolean;
  canBypassTierVisibility: boolean;
  canCreateBillingCard: boolean;
  canCreateWorkers: boolean;
  canEditIndividualComplianceList: boolean;
  canEditWorkers: boolean;
  canUpdateMembership: boolean;
  canUpdateMembershipAdmin: boolean;
  canSeeStaffingRequestRates: boolean;
  canShowAdminApprovalsItem: boolean;
  canShowAdminBilling: boolean;
  canShowAdminGroupsTab: boolean;
  canShowAdminMembers: boolean;
  canShowAdminOrganizationStructure: boolean;
  canShowAdminRatesOnDemandTab: boolean;
  canShowAdminRatesLongTermTab: boolean;
  canShowAdminStaffingRequestTemplates: boolean;
  canShowAdminSuppliers: boolean;
  canShowAdminTimekeepingHolidays: boolean;
  canShowAdminWorksListTemplates: boolean;
  canShowApplications: boolean;
  canShowApplicationsSubmitted: boolean;
  canShowAssignmentTermBillingRules: boolean;
  canShowDisputes: boolean;
  canShowDoNotRehires: boolean;
  canShowOffers: boolean;
  canShowGroupShiftMessaging: boolean;
  canShowInvoices: boolean;
  canShowLongTermAssignmentComments: boolean;
  canShowLongTermAssignments: boolean;
  canShowMissionControl: boolean;
  canShowOrganizationUnitIntegration: boolean;
  canShowOrganizationUnitShiftConfigs: boolean;
  canShowReverseInvoices: boolean;
  canShowShiftTargeting: boolean;
  canShowSidebarAdminItem: boolean;
  canShowSidebarBillingItem: boolean;
  canShowSidebarCandidatesItem: boolean;
  canShowSidebarOnboardingItem: boolean;
  canShowSidebarStaffingItem: boolean;
  canShowSidebarTeamItem: boolean;
  canShowSidebarTimesheetsItem: boolean;
  canShowTermsConditionsLink: boolean;
  canShowShifts: boolean;
  canShowTiering: boolean;
  canShowWorksLists: boolean;
  canShowWorkerStatus: boolean;
  canSubmitSubmittalPacketChangeRequest: boolean;
  canUpdateOffers: boolean;
  canUpdateApplications: boolean;
  canUpdateStaffingRequests: boolean;
  canUpdateAdminUsersUnits: boolean;
  canUpdateWorkerStatus: boolean;
  canUpdateStaffingRequestAssignments: boolean;
  canWithdrawApplications: boolean;
  canShowOrganizationOnDemandSettings: boolean;
  worksUser: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    saveLastVisitedPage: boolean | null;
    timeZone: string;
    initials: string;
    email: string;
    intercomUserHash: string;
    internalTrustedUser: boolean;
  };
  notificationCategories: {
    key: string;
    name: string;
  }[];
  notifications: NotificationType[];
};
